import axios from 'axios';
import { beginLoading, endLoading } from '../store/loading';

// JSON 통신 공통 인스턴스
const api = axios.create({
  baseURL: '/api',
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' }
});

// 에러 상세 표시 여부(백엔드 app.error.show-detail). false 면 모든 시스템 에러를 통일 문구로 표기.
const UNIFIED_ERROR = '네트워크 연결에 실패했습니다. 관리자에게 문의하세요.';
let showErrorDetail = true;   // 기본 현행. /api/config 로 1회 동기화(서버 미도달 시 기본값 유지)
axios.get('/api/config').then(r => {
  const v = r && r.data && r.data.data ? r.data.data.showErrorDetail : undefined;
  if (typeof v === 'boolean') showErrorDetail = v;
}).catch(() => {});

// 요청마다 현재 로그인 사용자 식별 헤더 부착 (권한 판정용, 데모)
// + 스펙24: 데이터 조회 동안 공통 로딩박스 표시 (요청 시작 시 카운트 증가)
api.interceptors.request.use(config => {
  const auth = window.__AUTH__;
  if (auth && auth.loginId) {
    config.headers['X-Login-Id'] = auth.loginId;
  }
  beginLoading();
  return config;
}, err => {
  endLoading();
  return Promise.reject(err);
});

// 공통 응답 언랩 (ApiResponse<T>) + 로딩박스 종료
api.interceptors.response.use(
  res => {
    endLoading();
    const body = res.data;
    if (body && body.success === false) {
      return Promise.reject(new Error(body.message || '요청 처리 실패'));
    }
    return body;
  },
  err => {
    endLoading();
    return Promise.reject(new Error(extractErrorMessage(err)));
  }
);

// 서버 응답 본문의 구체 메시지를 우선 표시하고, 없으면 상태코드별 한글 안내로 대체
//   (axios 기본 'Request failed with status code 403' 대신 사유가 보이도록)
function extractErrorMessage(err) {
  const resp = err && err.response;
  if (!resp) {
    // 응답 자체가 없음(네트워크/타임아웃 등) → 상세 숨김이면 통일 문구
    if (!showErrorDetail) return UNIFIED_ERROR;
    return (err && err.message) ? err.message : '서버에 연결할 수 없습니다. (네트워크 오류)';
  }
  const data = resp.data;
  if (data && typeof data === 'object' && data.message) {
    // 백엔드 ApiResponse.fail(message). 시스템에러는 백엔드가 이미 통일했고, 검증/권한 안내는 그대로 표시.
    return data.message;
  }
  if (data && typeof data === 'string' && data.trim() && data.length < 300) {
    // 평문/HTML 본문(예: 서블릿 500/404 HTML) → 상세 숨김이면 통일
    return showErrorDetail ? data.trim() : UNIFIED_ERROR;
  }
  // 본문 메시지가 없는 상태코드 → 상세 숨김이면 통일, 아니면 상태코드별 한글 안내
  if (!showErrorDetail) return UNIFIED_ERROR;
  const byStatus = {
    400: '잘못된 요청입니다.',
    401: '로그인이 필요합니다. 다시 로그인해 주세요.',
    403: '권한이 없습니다. (관리자 전용 기능이거나 로그인이 필요합니다)',
    404: '요청한 대상을 찾을 수 없습니다.',
    409: '중복 또는 충돌이 발생했습니다.',
    500: '서버 내부 오류가 발생했습니다.',
    502: '게이트웨이 오류입니다. 잠시 후 다시 시도해 주세요.',
    503: '서비스를 일시적으로 사용할 수 없습니다.'
  };
  return byStatus[resp.status] || ('요청 실패 (HTTP ' + resp.status + ')');
}

export default {
  // 인증
  login: (loginId, password) => api.post('/auth/login', { loginId, password }),
  changePassword: (loginId, currentPassword, newPassword) =>
    api.post('/auth/change-password', { loginId, currentPassword, newPassword }),

  // 포인트 조회
  points: params => api.get('/cashback/point', { params }),
  // 스펙56: 반품PV/반품철회PV 발생근거 (PV값+결정구간+배치일 반품 항목)
  refundBasis: (customerId, calcDt) => api.get('/cashback/refund-basis', { params: { customerId, calcDt } }),
  pointsTotals: params => api.get('/cashback/point/totals', { params }),
  pointsExport: params => api.get('/cashback/point/export', { params }),
  // 23002: 수당지급 다운로드(출금일=캘린더-8일 매칭, GRE_DAILY_CLOSING.except_tax + 고객 은행/계좌/이름)
  payoutExport: calDate => api.get('/cashback/payout/export', { params: { calDate } }),
  // 20003: 실 지급 금액(지급 이력 GRE_PAY_HIST) 조회 - 페이징
  payHist: params => api.get('/pay-hist', { params }),
  // 20003: 현재 로그인 사용자 정보(센터명/이름 등) - customer 초기값 프리필
  me: () => api.get('/me'),
  // 20003: 센터 검색 / 회원 검색 팝업(스펙 컬럼)
  searchCenterPopup: params => api.get('/search/center', { params }),
  searchMemberPopup: params => api.get('/search/member', { params }),
  // 20003: 센터 조회/추가/수정
  centerAdminList: params => api.get('/center-admin', { params }),
  centerAdminAdd: payload => api.post('/center-admin/add', payload),
  centerAdminUpdate: payload => api.post('/center-admin/update', payload),
  // 20003: 지사 조회/추가/수정
  branchAdminList: params => api.get('/branch-admin', { params }),
  branchAdminAdd: payload => api.post('/branch-admin/add', payload),
  branchAdminUpdate: payload => api.post('/branch-admin/update', payload),
  // 20003: 공통 코드 조회 + 은행코드 업로드
  comCodeTypes: () => api.get('/com-code-admin/types'),
  comCodeList: params => api.get('/com-code-admin', { params }),
  bankCodeUpload: rows => api.post('/com-code-admin/bank-upload', { rows }),
  // 20006: My Page (고객 정보) 조회/등록/저장 + 은행 콤보
  myPageInfo: customerId => api.get('/mypage', { params: { customerId } }),
  myPageBanks: () => api.get('/mypage/banks'),
  myPageRegister: payload => api.post('/mypage/register', payload),
  myPageSave: payload => api.post('/mypage/save', payload),
  closing: params => api.get('/cashback/closing', { params }),
  cashbackList: params => api.get('/cashback/list', { params }),
  summary: (customerId, calcDt) => api.get('/cashback/summary', { params: { customerId, calcDt } }),
  ranks: () => api.get('/cashback/ranks'),
  latest: () => api.get('/cashback/latest'),

  // 스펙234_개선_13: 고객수신(별도 분리 모듈)
  custReceiveCollect: dt => api.post('/cust-receive/collect', null, { params: dt ? { dt } : {} }),
  custReceiveTemp: () => api.get('/cust-receive/temp'),
  custReceiveClear: () => api.post('/cust-receive/temp/clear', {}),
  custReceiveTempDelete: ids => api.post('/cust-receive/temp/delete', { ids }),
  custReceiveApply: () => api.post('/cust-receive/apply', {}),
  custExcludeList: () => api.get('/cust-receive/exclude'),
  custExcludeAdd: loginId => api.post('/cust-receive/exclude/add', { loginId }),
  custExcludeDelete: ids => api.post('/cust-receive/exclude/delete', { ids }),
  // 스펙234_개선_41 [테스트] 아바타 강제생성
  avatarTestForce: payload => api.post('/avatar-test/force', payload),
  // [테스트] 불러오기 완료 임시 목록에 1건 추가
  custReceiveTestAdd: () => api.post('/cust-receive-test/temp-add', {}),

  // 고객 정보 추가(신규 등록, 최소항목) — staff/admin
  createCustomer: payload => api.post('/customer/create', payload),

  // 스펙234_개선_30: 주문사용자ID 편집/재조회
  orderUserIdInfo: customerId => api.get('/order-user-id/info', { params: { customerId } }),
  orderUserIdSave: payload => api.post('/order-user-id/save', payload),
  orderUserIdRescan: customerId => api.post('/order-user-id/rescan', { customerId }, { timeout: 300000 }),

  // 고객 조회
  customers: params => api.get('/customer/list', { params }),
  nodes: params => api.get('/customer/nodes', { params }),
  placementTree: params => api.get('/customer/placement-tree', { params: params || {} }),
  // 개선2_61: 멤버쉽 캐시백 지도(관리자)
  placementTreeMembership: params => api.get('/customer/placement-tree-membership', { params: params || {} }),
  // 3008: 멤버쉽 캐시백 지도(관리자) 기준일 조회 - 이력 스냅샷 구조 + 그날 멤버쉽 광고캐시백
  placementTreeMembershipHist: params => api.get('/customer/placement-tree-membership-hist', { params: params || {} }),
  // 스펙207: 추천 조직도(루트+2단, 클릭 확장)
  referralTree: params => api.get('/customer/referral-tree', { params: params || {} }),
  referralChildren: customerId => api.get('/customer/referral-children', { params: { customerId } }),
  // 3008: 조직도 "기준일/시점" 조회(이력 스냅샷 재현). params 에 snapType(BEFORE=수당계산직전/AFTER=수당계산직후) 포함.
  placementTreeHist: params => api.get('/customer/placement-tree-hist', { params: params || {} }),
  referralTreeHist: params => api.get('/customer/referral-tree-hist', { params: params || {} }),
  referralChildrenHist: (customerId, baseDate, snapType) => api.get('/customer/referral-children-hist', { params: { customerId, baseDate, snapType } }),
  // 3008: 시점 콤보용 - 이력 스냅샷 목록(배치일 dt + type)
  histSnapshotPoints: () => api.get('/customer/hist-snapshot-points'),
  // 3010: 조직도 네모 클릭(admin) - 노드 정보(사업자/소비자, 좌/우 추천자 수, 멤버쉽 스캔범위)
  nodeInfo: customerId => api.get('/customer/node-info', { params: { customerId } }),
  userNodes: targetLoginId => api.get('/customer/user-nodes', { params: targetLoginId ? { targetLoginId } : {} }),
  customer: id => api.get('/customer/' + id),
  importCustomers: (rows, actor) => api.post('/customer/import', { rows, actor }),
  changeReferrer: payload => api.post('/customer/change-referrer', payload),
  exportCustomers: params => api.get('/customer/export', { params }),

  // 후원인(좌/우) 수정 + 공통 회원 검색
  referralSearch: params => api.get('/customer/referral/search', { params }),
  searchCustomers: params => api.get('/customer/referral/search', { params }),
  referralSave: payload => api.post('/customer/referral/save', payload),
  referralUnset: payload => api.post('/customer/referral/unset', payload),

  // 스펙102: 고객정보 수정(후원인=부모 단일, 추천인=추천 단일)
  editInfo: customerId => api.get('/customer/edit-info/' + customerId),
  sponsorSet: payload => api.post('/customer/sponsor/set', payload),
  sponsorUnset: payload => api.post('/customer/sponsor/unset', payload),
  referrerSet: payload => api.post('/customer/referrer/set', payload),
  referrerUnset: payload => api.post('/customer/referrer/unset', payload),
  // 스펙30: 아바타 노드 후원인(좌/우)/추천인 수정
  nodeEditInfo: nodeId => api.get('/customer/node-edit/' + nodeId),
  nodeSponsorSave: payload => api.post('/customer/node/sponsor/save', payload),
  nodeSponsorUnset: payload => api.post('/customer/node/sponsor/unset', payload),
  nodeRefSave: payload => api.post('/customer/node/ref/save', payload),
  nodeRefUnset: payload => api.post('/customer/node/ref/unset', payload),
  nodeRefClearAll: payload => api.post('/customer/node/ref/clear-all', payload),

  // 추천인 수정 (스펙 07/09)
  directRefList: sponsorId => api.get('/customer/direct-ref/' + sponsorId),
  directRefSave: payload => api.post('/customer/direct-ref/save', payload),
  directRefUnset: payload => api.post('/customer/direct-ref/unset', payload),
  directRefClearAll: payload => api.post('/customer/direct-ref/clear-all', payload),

  // 소속 센터 검색/수정/업로드 (스펙20)
  centerSearch: params => api.get('/customer/center/search', { params }),
  centerSave: payload => api.post('/customer/center/save', payload),
  centerImport: (rows, actor) => api.post('/customer/center/import', { rows, actor }),
  importCustomers102: (rows, actor) => api.post('/customer/import102', { rows, actor }, { timeout: 300000 }),   // 대량 업로드 대비 5분

  // cafe24 운영자 로그인 / 로그인2(쇼핑몰 회원)
  cafe24LoginOptions: () => api.get('/cafe24/login-options'),
  cafe24OauthUrl: () => api.get('/cafe24/oauth/url'),
  cafe24MallLoginUrl: () => api.get('/cafe24/oauth/mall-login-url'),

  // cafe24 API 조회 (스펙13)
  cafe24QueryOrders: params => api.get('/cafe24/query/orders', { params }),
  cafe24QueryRefunds: params => api.get('/cafe24/query/refunds', { params }),
  cafe24QueryOrdersTest: params => api.get('/cafe24/query/orders-test', { params }),  // v111: embed=refunds

  // 스펙46: 배치 결과 확인 / 기준표 조회(센터목록·등급계산율·직급·센터지원율)
  batchResults: params => api.get('/batch/results', { params }),
  refList: name => api.get('/ref/' + name),

  // 구매내역 수동 저장 (스펙29, admin)
  manualPurchaseList: params => api.get('/purchase-manual/list', { params }),  // {startDt,endDt,name,keyword,order_id}
  manualPurchaseGet: id => api.get('/purchase-manual/' + id),
  manualPurchaseSave: payload => api.post('/purchase-manual/save', payload),
  manualPurchaseDelete: id => api.post('/purchase-manual/delete', { id }),
  manualPurchaseDeleteAll: params => api.post('/purchase-manual/delete-all', params || {}),
  manualPurchaseApply: () => api.post('/purchase-manual/apply', {}),
  // 스펙234_개선_14: 주문 불러오기(별도 분리 모듈) + 불러오기 제외할 cafe24 주문
  orderReceiveCollect: dt => api.post('/order-receive/collect', null, { params: dt ? { dt } : {} }),
  orderListSearch: (loginId, startDt, endDt) => api.get('/order-receive/order-list', { params: { loginId, startDt, endDt } }),
  orderExcludeList: () => api.get('/order-receive/exclude'),
  orderExcludeAdd: orderNo => api.post('/order-receive/exclude/add', { orderNo }),
  orderExcludeDelete: ids => api.post('/order-receive/exclude/delete', { ids }),
  importManualPurchases: rows => api.post('/purchase-manual/import', { rows }),   // 스펙201: 수동저장 목록 엑셀 업로드
  manualNextOrder: (targetLoginId, date) => api.get('/purchase-manual/next-order', { params: { targetLoginId, date } }),
  manualEstimatePv: (targetLoginId, startDt, endDt) => api.get('/purchase-manual/estimate-pv', { params: { targetLoginId, startDt, endDt } }),
  manualEstimatePvNodes: (targetLoginId, startDt, endDt) => api.get('/purchase-manual/estimate-pv-nodes', { params: { targetLoginId, startDt, endDt } }),
  // 스펙54: 반품/반품철회 저장 확인용 - 자동 산출 범위 + 노드별 예상 반환금액 ({startDt,endDt,rows})
  manualEstimateRetPv: (targetLoginId, orderNo, buyDt, work) => api.get('/purchase-manual/estimate-ret-pv', { params: { targetLoginId, order_id: orderNo, buyDt, work } }),
  // 스펙49: 결제내역 조회
  paymentHistory: params => api.get('/payment-history', { params }),  // {name, loginId, startDate, endDate}
  runBatch: dt => api.get('/batch/run', { params: { dt } }),

  // 28002: SMS 발송(수동) / SMS 발송 이력 조회 (분리 모듈)
  smsTempList: params => api.get('/sms-temp/list', { params: params || {} }),
  smsTargetPurge: () => api.post('/sms-purge/run', {}),
  smsTargetExtract: dt => api.post('/sms-target/extract', null, { params: dt ? { dt } : {} }),
  smsSendTemp: items => api.post('/sms-send/send', { items }),
  smsHistList: params => api.get('/sms-hist/list', { params: params || {} }),
  // 29007: 엑셀 생성은 프론트에서(xlsx-js-style). 서버는 JSON 행만 반환.
  smsHistSearchRows: params => api.get('/sms-hist-search-download/rows', { params: params || {} }),
  smsHistSuccessRows: params => api.get('/sms-hist-download/success', { params: params || {} })
};
